import { polygonBounds } from './geometry.ts';
import {
  add,
  length,
  lengthSq,
  scale,
  segmentIntersection,
  segmentsIntersect,
  sub,
} from './math.ts';
import { plateauProbesForMap } from './map/elevationStack.ts';
import { pointInColumnAabb, pointUnderPlateauProbe } from './map/extrude.ts';
import { pointInPolygon } from './map/islands.ts';
import { tessellateRing } from './map/ring.ts';
import type { MapBounds, MapDocument, Ring, Vec2 } from './map/types.ts';
import type { MovePath } from './path.ts';
import { pathFromPolyline, sampleMovePath } from './path.ts';

export type CollisionLayer =
  'island' | 'rock' | 'bounds' | 'shallow';

export type CollisionContact = {
  layer: CollisionLayer;
  /** Map object id, or null for bounds. */
  id: string | null;
  solid: boolean;
  point: Vec2;
  /** 0..1 along the sampled path. */
  t: number;
};

export type PathCollisions = {
  blocked: boolean;
  contacts: CollisionContact[];
};

type RingCollider = {
  layer: CollisionLayer;
  id: string;
  solid: boolean;
  outline: Vec2[];
};

function contactKey(layer: CollisionLayer, id: string | null): string {
  return `${layer}:${id ?? ''}`;
}

function outOfBounds(point: Vec2, bounds: MapBounds): boolean {
  return (
    point.x < 0 ||
    point.x > bounds.width ||
    point.y < 0 ||
    point.y > bounds.height
  );
}

function pointInRing(point: Vec2, ring: Ring): boolean {
  return pointInPolygon(point, tessellateRing(ring));
}

/** Islands and rocks — the bodies that sit on the water, not reefs, shallows, or the void. */
export function pointInSurfaceObstacle(map: MapDocument, point: Vec2): boolean {
  return (
    map.islands.some((island) => pointInRing(point, island)) ||
    map.rocks.some((rock) => pointInRing(point, rock))
  );
}

let underCapWalks = 0;

/**
 * Cap outlines whose extruded box a `pointUnderColumnCap` sample actually reached.
 * AABB misses do not increment. The hover budget guard fails if a hover walks every
 * distant ring. Nothing reads this in production.
 */
export function underCapRingWalks(): number {
  return underCapWalks;
}

/** True when `point` is legal water sitting under an island or rock cap. */
export function pointUnderColumnCap(map: MapDocument, point: Vec2): boolean {
  for (const probe of plateauProbesForMap(map)) {
    if (!probe.box || probe.top === probe.footprint || !pointInColumnAabb(point, probe.box)) {
      continue;
    }

    underCapWalks += 1;

    if (pointUnderPlateauProbe(probe, point)) {
      return true;
    }
  }

  return false;
}

export function pointInSolid(map: MapDocument, point: Vec2): boolean {
  if (outOfBounds(point, map.bounds)) {
    return true;
  }

  return pointInSurfaceObstacle(map, point);
}

function ringColliders(map: MapDocument): RingCollider[] {
  return [
    ...map.islands.map((island) => ({
      layer: 'island' as const,
      id: island.id,
      solid: true,
      outline: tessellateRing(island),
    })),
    ...map.rocks.map((rock) => ({
      layer: 'rock' as const,
      id: rock.id,
      solid: true,
      outline: tessellateRing(rock),
    })),
    ...map.shallowWater.map((zone) => ({
      layer: 'shallow' as const,
      id: zone.id,
      solid: false,
      outline: tessellateRing(zone),
    })),
  ];
}

function segmentHitsClosedPolyline(
  from: Vec2,
  to: Vec2,
  outline: readonly Vec2[],
): boolean {
  if (outline.length < 2) {
    return false;
  }

  for (let i = 0; i < outline.length; i += 1) {
    const edgeFrom = outline[i];
    const edgeTo = outline[(i + 1) % outline.length];

    if (edgeFrom && edgeTo && segmentsIntersect(from, to, edgeFrom, edgeTo)) {
      return true;
    }
  }

  return false;
}

function boundEdges(bounds: MapBounds): [Vec2, Vec2][] {
  const { width, height } = bounds;
  return [
    [
      { x: 0, y: 0 },
      { x: width, y: 0 },
    ],
    [
      { x: width, y: 0 },
      { x: width, y: height },
    ],
    [
      { x: width, y: height },
      { x: 0, y: height },
    ],
    [
      { x: 0, y: height },
      { x: 0, y: 0 },
    ],
  ];
}

function segmentHitsBounds(from: Vec2, to: Vec2, bounds: MapBounds): boolean {
  return boundEdges(bounds).some(([a, b]) => segmentsIntersect(from, to, a, b));
}

let ringWalks = 0;

/**
 * Rings walked by `classifySamples` since load, for the hover budget guard in
 * `tests/unit/hoverBudget.test.ts`. A bounds reject that stops working shows up here as a hover
 * walking every ring on the map rather than the handful near the path. Nothing reads it in
 * production.
 */
export function collisionRingWalks(): number {
  return ringWalks;
}

/**
 * Rings whose bounding box the path can reach. Every sample point and every segment is tested
 * against every surviving ring's full outline, so on a map with two dozen islands this reject is
 * what keeps a hover from walking geometry it could never touch. An AABB miss is conclusive: a
 * path outside a ring's bounds can neither be inside it nor cross its edges.
 */
function ringsNearPath(
  rings: readonly RingCollider[],
  points: readonly Vec2[],
): readonly RingCollider[] {
  const path = polygonBounds(points);

  if (!path) {
    return rings;
  }

  return rings.filter((ring) => {
    const bounds = polygonBounds(ring.outline);

    return (
      bounds !== null &&
      bounds.minX <= path.maxX &&
      bounds.maxX >= path.minX &&
      bounds.minY <= path.maxY &&
      bounds.maxY >= path.minY
    );
  });
}

function classifySamples(
  map: MapDocument,
  points: readonly Vec2[],
  start: Vec2,
): PathCollisions {
  const contacts: CollisionContact[] = [];
  const seen = new Set<string>();
  const rings = ringsNearPath(ringColliders(map), points);
  ringWalks += rings.length;
  const denom = Math.max(1, points.length - 1);

  const record = (
    layer: CollisionLayer,
    id: string | null,
    solid: boolean,
    point: Vec2,
    t: number,
  ): void => {
    const key = contactKey(layer, id);

    if (seen.has(key)) {
      return;
    }

    seen.add(key);
    contacts.push({ layer, id, solid, point: { x: point.x, y: point.y }, t });
  };

  for (let i = 0; i < points.length; i += 1) {
    const point = points[i];

    if (!point) {
      continue;
    }

    const t = i / denom;

    if (outOfBounds(point, map.bounds)) {
      record('bounds', null, true, point, t);
    }

    for (const ring of rings) {
      if (
        !seen.has(contactKey(ring.layer, ring.id)) &&
        pointInPolygon(point, ring.outline)
      ) {
        record(ring.layer, ring.id, ring.solid, point, t);
      }
    }
  }

  for (let i = 0; i < points.length - 1; i += 1) {
    const from = points[i];
    const to = points[i + 1];

    if (!from || !to) {
      continue;
    }

    const t = (i + 1) / denom;

    if (
      !seen.has(contactKey('bounds', null)) &&
      segmentHitsBounds(from, to, map.bounds)
    ) {
      record('bounds', null, true, to, t);
    }

    for (const ring of rings) {
      if (
        !seen.has(contactKey(ring.layer, ring.id)) &&
        segmentHitsClosedPolyline(from, to, ring.outline)
      ) {
        record(ring.layer, ring.id, ring.solid, to, t);
      }
    }
  }

  const last = points.at(-1) ?? start;
  const startInSolid = pointInSolid(map, start);
  const destInSolid = pointInSolid(map, last);
  const anySolid = contacts.some((contact) => contact.solid);

  return {
    blocked: startInSolid ? destInSolid : anySolid,
    contacts,
  };
}

/**
 * Walks the sampled move path against map solids and soft layers. Solid hits block unless the
 * boat already overlaps a solid and the destination is still in water.
 */
export function classifyPath(
  map: MapDocument,
  path: MovePath,
  start: Vec2,
): PathCollisions {
  const sampled = sampleMovePath(path);
  return classifySamples(map, sampled.length > 0 ? sampled : [start], start);
}

/** Same as `classifyPath` when a move has a path; a zero-length click is a single sample. */
export function classifyDestination(
  map: MapDocument,
  start: Vec2,
  destination: Vec2,
  path: MovePath | null,
): PathCollisions {
  if (path) {
    return classifyPath(map, path, start);
  }

  return classifySamples(map, [start, destination], start);
}

/** Park just on the water side of a shoreline so the next turn is not `startInSolid`. */
const SHORE_NUDGE_PX = 0.5;

export type SolidClip = {
  point: Vec2;
  path: MovePath;
  /** Path direction on the segment that met the shore. */
  approach: Vec2;
  /** The island, rock, or map-edge segment that was crossed. */
  edge: { from: Vec2; to: Vec2 };
};

type SegmentHit = {
  segmentIndex: number;
  point: Vec2;
  edgeFrom: Vec2;
  edgeTo: Vec2;
};

/**
 * Cuts the aimed path at the first island, rock, or map-edge intersection and nudges the
 * landed point back into water. Does not increment `collisionRingWalks` — hover already
 * paid for classify; this is the crash refinement.
 */
export function clipPathToSolid(
  map: MapDocument,
  path: MovePath,
  start: Vec2,
): SolidClip | null {
  const sampled = sampleMovePath(path);
  const points = sampled.length > 0 ? sampled : [start];
  const hit = firstSolidIntersection(map, points);

  if (!hit) {
    return null;
  }

  const from = points[hit.segmentIndex];
  const to = points[hit.segmentIndex + 1];

  if (!from || !to) {
    return null;
  }

  const landed = nudgeOffSolid(map, from, hit.point);
  const prefix = points.slice(0, hit.segmentIndex + 1);
  const last = prefix.at(-1);

  if (!last || last.x !== landed.x || last.y !== landed.y) {
    prefix.push(landed);
  }

  const clipped = pathFromPolyline(prefix, path.kind);

  if (!clipped) {
    return null;
  }

  return {
    point: landed,
    path: clipped,
    approach: sub(to, from),
    edge: { from: { ...hit.edgeFrom }, to: { ...hit.edgeTo } },
  };
}

function firstSolidIntersection(
  map: MapDocument,
  points: readonly Vec2[],
): SegmentHit | null {
  const solids = ringsNearPath(
    ringColliders(map).filter((ring) => ring.solid),
    points,
  );

  for (let i = 0; i < points.length - 1; i += 1) {
    const from = points[i];
    const to = points[i + 1];

    if (!from || !to) {
      continue;
    }

    const hits: Array<{ point: Vec2; edgeFrom: Vec2; edgeTo: Vec2 }> = [];

    for (const [edgeFrom, edgeTo] of boundEdges(map.bounds)) {
      const point = segmentIntersection(from, to, edgeFrom, edgeTo);

      if (point) {
        hits.push({ point, edgeFrom, edgeTo });
      }
    }

    for (const ring of solids) {
      const outline = ring.outline;

      for (let e = 0; e < outline.length; e += 1) {
        const edgeFrom = outline[e];
        const edgeTo = outline[(e + 1) % outline.length];

        if (!edgeFrom || !edgeTo) {
          continue;
        }

        const point = segmentIntersection(from, to, edgeFrom, edgeTo);

        if (point) {
          hits.push({ point, edgeFrom, edgeTo });
        }
      }
    }

    let nearest: (typeof hits)[number] | null = null;
    let nearestAlong = Number.POSITIVE_INFINITY;

    for (const hit of hits) {
      const along = lengthSq(sub(hit.point, from));

      if (along < nearestAlong) {
        nearest = hit;
        nearestAlong = along;
      }
    }

    if (nearest) {
      return {
        segmentIndex: i,
        point: nearest.point,
        edgeFrom: nearest.edgeFrom,
        edgeTo: nearest.edgeTo,
      };
    }
  }

  return null;
}

function nudgeOffSolid(map: MapDocument, from: Vec2, hit: Vec2): Vec2 {
  const back = sub(from, hit);
  const mag = length(back);

  if (mag < 1e-9) {
    return { x: from.x, y: from.y };
  }

  const landed = add(hit, scale(back, SHORE_NUDGE_PX / mag));

  if (!pointInSolid(map, landed)) {
    return landed;
  }

  const mid = add(hit, scale(back, Math.min(mag * 0.5, 2) / mag));

  if (!pointInSolid(map, mid)) {
    return mid;
  }

  return { x: from.x, y: from.y };
}
